import { useEpvFamilies } from '@/hooks/api';
import { formatNum, getEpvFamilyById, isInRange } from '@/utils/utils';
import { ChartDataset, ScriptableContext } from 'chart.js';
import React, { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import { generateColorPalette } from '../scenario/capacity/CapacityChart';

interface MatchingSatisfactionProps {
	data: TMatchingData | undefined;
	yearRange: [number, number];
	processStep: TValue;
	by: TValue;
	displays: TValue[];
	selectedEpvFamilies: TValue[];
	selectedProcessStep: TProcessStep | undefined;
}

type TChartDataSet = ChartDataset<'bar' | 'line', number[]>;

const palette = generateColorPalette();

const MatchingSatisfaction: React.FC<MatchingSatisfactionProps> = ({
	data,
	yearRange,
	processStep,
	by,
	displays,
	selectedEpvFamilies,
	selectedProcessStep,
}) => {
	const isGlobalRegions = by === 'global regions';
	const isProductionRegion = by === 'production region';
	const isPlant = by === 'plant';

	const { data: epvFamilies } = useEpvFamilies();

	const years = useMemo(
		() =>
			Array.from(
				{ length: yearRange[1] - yearRange[0] + 1 },
				(_, i) => i + yearRange[0]
			),
		[data]
	);

	const getDemandDeltas = () => {
		if (!data) return {};

		const deltasByYear: Record<number, Record<number, number>> = {};
		for (const d of data.demand_fulfilled) {
			const {
				year,
				epv_family_lvl2_id,
				global_region,
				production_region,
				matching,
			} = d;
			if (!isInRange(year, yearRange)) continue;

			const fullfilledKey = `${processStep}_fulfilled`;
			const fullfilled = d[fullfilledKey as keyof typeof d] as number;
			const demandKey = `${processStep}_demand`;
			const demand = d[demandKey as keyof typeof d] as number;

			if (isGlobalRegions && !displays.includes(global_region)) continue;
			if (isProductionRegion && !displays.includes(production_region)) continue;
			if (isPlant && !displays.includes(matching)) continue;
			if (
				selectedProcessStep?.showCapacitiesIfEpvFamilyFiltered &&
				selectedEpvFamilies.length > 0 &&
				!selectedEpvFamilies.includes(epv_family_lvl2_id)
			)
				continue;

			if (deltasByYear[year]) {
				if (deltasByYear[year][epv_family_lvl2_id]) {
					deltasByYear[year][epv_family_lvl2_id] += fullfilled - demand;
				} else {
					deltasByYear[year][epv_family_lvl2_id] = fullfilled - demand;
				}
			} else {
				deltasByYear[year] = { [epv_family_lvl2_id]: fullfilled - demand };
			}
		}

		return deltasByYear;
	};

	const getCapaDeltas = () => {
		if (!data) return {};

		const deltasByYear: Record<number, number> = {};
		for (const c of data.capacity_used) {
			const {
				year,
				capacity,
				capacity_used,
				capacity_type,
				global_region,
				production_region,
				matching,
				epv_family_lvl2_ids,
			} = c;
			if (!isInRange(year, yearRange)) continue;
			if (isGlobalRegions && !displays.includes(global_region)) continue;
			if (isProductionRegion && !displays.includes(production_region)) continue;
			if (isPlant && !displays.includes(matching)) continue;
			if (
				selectedProcessStep?.showCapacitiesIfEpvFamilyFiltered &&
				selectedEpvFamilies.length > 0 &&
				!epv_family_lvl2_ids.every((id) => selectedEpvFamilies.includes(id))
			)
				continue;

			if (capacity_type === processStep) {
				if (deltasByYear[year]) {
					deltasByYear[year] += capacity - capacity_used;
				} else {
					deltasByYear[year] = capacity - capacity_used;
				}
			}
		}

		return deltasByYear;
	};

	const getTotalCapacityByYear = () => {
		if (!data) return {};

		const totalCapacityByYear: Record<number, number> = {};

		for (const c of data.capacity_used) {
			const {
				year,
				capacity,
				capacity_type,
				global_region,
				production_region,
				matching,
				epv_family_lvl2_ids,
			} = c;
			if (!isInRange(year, yearRange)) continue;
			if (isGlobalRegions && !displays.includes(global_region)) continue;
			if (isProductionRegion && !displays.includes(production_region)) continue;
			if (isPlant && !displays.includes(matching)) continue;
			if (
				selectedProcessStep?.showCapacitiesIfEpvFamilyFiltered &&
				selectedEpvFamilies.length > 0 &&
				!epv_family_lvl2_ids.every((id) => selectedEpvFamilies.includes(id))
			)
				continue;
			if (capacity_type === processStep) {
				if (totalCapacityByYear[year]) {
					totalCapacityByYear[year] += capacity;
				} else {
					totalCapacityByYear[year] = capacity;
				}
			}
		}

		return totalCapacityByYear;
	};

	const convertDemandDeltasyYearToChartDatasets = (
		data: Record<number, Record<number, number>>
	) => {
		const datasets: TChartDataSet[] = [];
		const years = Object.keys(data);
		if (!years.length) return [];

		const epvs = Object.keys(data[years[0] as any]);
		for (const epvFamily of epvs) {
			const f = getEpvFamilyById(epvFamilies, +epvFamily);
			const backgroundColor = palette[epvs.indexOf(epvFamily)];
			const dataset: TChartDataSet = {
				type: 'bar',
				backgroundColor,
				label: `Missing capacity ${f?.epv_family_lvl2}`,
				data: [],
			};

			for (const year of years) {
				dataset.data.push(data[+year][epvFamily as any]);
			}

			datasets.push(dataset);
		}

		return datasets;
	};

	const convertTotalDemandByYearToChartDatasets = (
		data: Record<number, Record<number, number>>
	) => {
		const datasets: ChartDataset[] = [];
		const keys = Object.keys(data);

		if (!keys.length) return [];

		const indexKeys = Object.keys(data[keys[0] as any]);
		for (const indexKey of indexKeys) {
			const f = getEpvFamilyById(epvFamilies, +indexKey);
			const color = palette[indexKeys.indexOf(indexKey)];
			const backgroundColor = color + '20';
			const dataset: ChartDataset = {
				label: `Demand - ${f?.epv_family_lvl2}`,
				borderColor: color,
				backgroundColor,
				type: 'line',
				fill: 'origin',
				pointRadius: 0,
				data: [],
				order: 1,
			};

			for (const year of keys) {
				dataset.data.push(data[+year][indexKey as any]);
			}

			datasets.push(dataset);
		}
		return datasets.reverse();
	};

	const getTotalDemandByYear = () => {
		if (!data || !processStep) return {};

		const totalDemandByYear: Record<number, Record<number, number>> = {};
		for (const d of data.demand_fulfilled) {
			const {
				year,
				epv_family_lvl2_id,
				global_region,
				production_region,
				matching,
			} = d;
			if (!isInRange(year, yearRange)) continue;
			const key = `${processStep}_demand`;
			const demand = d[key as keyof typeof d] as number;

			if (isGlobalRegions && !displays.includes(global_region)) continue;
			if (isProductionRegion && !displays.includes(production_region)) continue;
			if (isPlant && !displays.includes(matching)) continue;
			if (
				selectedProcessStep?.showCapacitiesIfEpvFamilyFiltered &&
				selectedEpvFamilies.length > 0 &&
				!selectedEpvFamilies.includes(epv_family_lvl2_id)
			)
				continue;

			if (totalDemandByYear[year]) {
				if (totalDemandByYear[year][epv_family_lvl2_id]) {
					totalDemandByYear[year][epv_family_lvl2_id] += demand;
				} else {
					totalDemandByYear[year][epv_family_lvl2_id] = demand;
				}
			} else {
				totalDemandByYear[year] = { [epv_family_lvl2_id]: demand };
			}
		}

		return totalDemandByYear;
	};

	if (!data || !processStep) return null;

	return (
		<div>
			<Chart
				style={{
					maxHeight: '500px',
				}}
				type='bar'
				data={{
					labels: years,
					datasets: [
						{
							type: 'line',
							data: Object.values(getTotalCapacityByYear()),
							pointRadius: 0,
							stack: 'stack1',
							fill: false,
							borderColor: 'black',
							borderDash: [3, 3],
							label: 'Total capacity',
						},
						{
							label: 'Remaining capacity',
							type: 'bar',
							data: Object.values(getCapaDeltas()),
							backgroundColor: (ctx: ScriptableContext<'bar'>) => {
								const value = ctx.dataset.data[ctx.dataIndex] as number;
								return value < 0
									? '#bd2015'
									: value >= 0.5
									? '#009a52'
									: '#fee2ce';
							},
						},
						...convertDemandDeltasyYearToChartDatasets(getDemandDeltas()),
						...convertTotalDemandByYearToChartDatasets(getTotalDemandByYear()),
					],
				}}
				options={{
					scales: {
						y: {
							stacked: true,
						},
						x: {
							stacked: true,
						},
					},
					plugins: {
						legend: {
							display: true,
						},
						title: {
							display: true,
						},
						tooltip: {
							callbacks: {
								label: (ctx) => {
									const index = ctx.dataIndex;
									const value = ctx.dataset.data[index] as number;
									const label = ctx.dataset.label;
									return label + ' ' + formatNum({ value });
								},
							},
						},
						zoom: {
							zoom: {
								mode: 'y',
								wheel: {
									enabled: true,
								},
							},
						},
					},
					interaction: {
						intersect: false,
					},
				}}
			/>
		</div>
	);
};

export default MatchingSatisfaction;
